import React, { useEffect, useState } from 'react';
import { useFeatureFlags } from '../../context/FeatureFlagsContext';
import { db } from '../../utils/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import ImageUploader from './common/ImageUploader';

const AdminSettings: React.FC = () => {
  const { flags, loading, setFlag } = useFeatureFlags();

  // Branding (header logo) — stored in content/branding (public read, admin write)
  const [logoUrl, setLogoUrl] = useState('');
  const [brandingLoading, setBrandingLoading] = useState(true);
  const [brandingMessage, setBrandingMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const snap = await getDoc(doc(db, 'content', 'branding'));
        if (snap.exists()) {
          setLogoUrl(snap.data().logoUrl || '');
        }
      } catch (error) {
        console.error('Error fetching branding:', error);
      } finally {
        setBrandingLoading(false);
      }
    };
    fetchBranding();
  }, []);

  const saveLogo = async (url: string) => {
    setLogoUrl(url);
    try {
      await setDoc(doc(db, 'content', 'branding'), {
        logoUrl: url,
        updatedAt: new Date().toISOString()
      }, { merge: true });
      setBrandingMessage({ type: 'success', text: url ? 'Logo saved. It appears in the site header immediately.' : 'Logo cleared — the header falls back to the built-in mark.' });
    } catch (error) {
      console.error('Error saving branding:', error);
      setBrandingMessage({ type: 'error', text: 'Failed to save logo.' });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin h-8 w-8 border-4 border-black border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-heading tracking-widest mb-6">SETTINGS</h2>

      {/* Branding */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 max-w-2xl mb-8">
        <h3 className="text-lg font-heading tracking-widest mb-4">BRANDING</h3>

        {brandingMessage && (
          <div className={`p-3 mb-4 text-sm ${brandingMessage.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {brandingMessage.text}
          </div>
        )}

        {brandingLoading ? (
          <div className="animate-spin h-6 w-6 border-4 border-black border-t-transparent rounded-full" />
        ) : (
          <>
            <ImageUploader
              label="Header Logo"
              value={logoUrl}
              onChange={saveLogo}
              pathPrefix="content/branding"
              aspectClass="aspect-[3/1]"
              onError={(text) => setBrandingMessage({ type: 'error', text })}
            />
            <p className="text-xs text-gray-500 mt-3">
              Use a dark logo on a transparent background (SVG or PNG). Over the home hero the header
              automatically renders it white; on light pages it shows as uploaded. Clearing this field
              restores the built-in 4HVNSK8 mark.
            </p>
          </>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6 max-w-2xl">
        <h3 className="text-lg font-heading tracking-widest mb-4">FEATURE FLAGS</h3>

        <div className="flex items-center justify-between py-4 border-b border-gray-100">
          <div>
            <p className="font-medium text-sm">NFT Minting</p>
            <p className="text-xs text-gray-500 mt-1">
              When enabled, skateboard purchases automatically mint a companion NFT.
              Wallet address collection will appear in the checkout flow.
            </p>
          </div>
          <button
            onClick={() => setFlag('nftEnabled', !flags.nftEnabled)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              flags.nftEnabled ? 'bg-black' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                flags.nftEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        <div className="mt-4 px-3 py-2 rounded bg-gray-50 text-xs text-gray-500">
          {flags.nftEnabled
            ? 'NFT minting is ACTIVE. Skateboard orders will trigger NFT minting in the webhook.'
            : 'NFT minting is OFF. Skateboard orders will be fulfilled without minting. Orders will be tagged so they can be retroactively minted later.'}
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
